import { Link } from "react-router-dom";
import { ArrowUp, MessageSquare } from "lucide-react";
import ShareMenu from "./ShareMenu";
import { avatarUrl } from "../lib/avatar";

const TAG_COLORS = {
  wins:      { bg: "#FFD600", color: "#121212" },
  question:  { bg: "#2B5CFF", color: "#fff" },
  advice:    { bg: "#FF3333", color: "#fff" },
  resource:  { bg: "#121212", color: "#FFD600" },
  rant:      { bg: "#FF3333", color: "#fff" },
  microsaas: { bg: "#FFD600", color: "#121212" },
  freelance: { bg: "#2B5CFF", color: "#fff" },
  hackathon: { bg: "#FF3333", color: "#fff" },
  workshops: { bg: "#FFD600", color: "#121212" },
  notiontemplates: { bg: "#121212", color: "#fff" },
};

function timeAgo(ts) {
  const diff = (Date.now() - new Date(ts)) / 1000;
  if (diff < 60)  return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

export default function ForumPostCard({ post, onVote, votedIds = new Set() }) {
  const hasVoted = votedIds.has(post.id);
  const isHot    = post.upvotes >= 30;

  return (
    <article
      className="brutal-card p-4 sm:p-5 flex gap-4 hover:translate-x-[2px] hover:translate-y-[-2px] transition-transform"
      data-testid={`forum-post-${post.id}`}
    >
      {/* VOTE COLUMN */}
      <div className="flex flex-col items-center gap-1 shrink-0 pt-0.5">
        <button
          onClick={() => onVote && onVote(post)}
          className={`brutal-btn text-xs px-2 py-1 flex flex-col items-center gap-0.5 ${
            hasVoted ? "brutal-btn-yellow" : ""
          }`}
          data-testid={`vote-btn-${post.id}`}
          title={hasVoted ? "Remove vote" : "Upvote"}
        >
          <ArrowUp size={16} strokeWidth={hasVoted ? 3 : 2} />
          <span className="font-display font-extrabold text-lg leading-none">{post.upvotes}</span>
        </button>
      </div>

      {/* CONTENT COLUMN */}
      <div className="flex-1 min-w-0">
        {/* Tags + hot badge */}
        <div className="flex flex-wrap gap-1.5 mb-2 items-center">
          {isHot && (
            <span className="font-mono text-[10px] uppercase tracking-widest bg-marker-red text-white border border-marker-red px-1.5 py-0.5">
              🔥 HOT
            </span>
          )}
          {post.is_pinned && (
            <span className="font-mono text-[10px] uppercase tracking-widest bg-ink text-cream border border-ink px-1.5 py-0.5">
              📌 PINNED
            </span>
          )}
          {(post.tags || []).map((t) => {
            const style = TAG_COLORS[t] || { bg: "#FFD600", color: "#121212" };
            return (
              <span key={t} className="tag-pill text-[11px]"
                style={{ background: style.bg, color: style.color }}>
                #{t}
              </span>
            );
          })}
        </div>

        {/* Title */}
        <Link to={`/forum/${post.id}`} className="block">
          <h2 className="font-display font-extrabold text-xl sm:text-2xl uppercase tracking-tight leading-tight hover:underline decoration-marker-red decoration-2 underline-offset-2">
            {post.title}
          </h2>
        </Link>

        {/* Body preview */}
        {post.body && (
          <p className="mt-1.5 text-sm leading-relaxed text-ink/80 line-clamp-2">
            {post.body.replace(/\*\*/g, "").replace(/---/g, "").split("\n")[0]}
          </p>
        )}

        {/* Meta row */}
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <Link to={`/u/${post.author?.id}`} className="flex items-center gap-2 hover:underline underline-offset-2">
            <img src={avatarUrl(post.author?.avatar_url, post.author?.id)} alt={post.author?.name}
              className="w-6 h-6 border border-ink blob bg-cream" />
            <span className="font-display font-extrabold text-sm uppercase tracking-tight">{post.author?.name}</span>
            <span className="font-mono text-[11px] text-ink/50">{post.author?.participant_id}</span>
          </Link>
          <span className="font-mono text-[11px] text-ink/50">·</span>
          <span className="font-mono text-[11px] text-ink/50">{timeAgo(post.created_at)}</span>
          <Link to={`/forum/${post.id}`}
            className="ml-auto flex items-center gap-1 font-mono text-[11px] text-ink/60 hover:text-ink"
            data-testid={`reply-count-${post.id}`}>
            <MessageSquare size={13} /> {post.reply_count} {post.reply_count === 1 ? "reply" : "replies"}
          </Link>
          <ShareMenu
            url={`/forum/${post.id}`}
            title={post.title}
            compact
          />
        </div>
      </div>
    </article>
  );
}

export { TAG_COLORS, timeAgo };
